import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { Balance, SearchOptions, Transaction } from '@partiri-cloud/types'

/** Display details of a saved Mollie mandate. No address — that comes from the
 *  workspace's active billing entity, not the card. */
export interface SavedPaymentMethod {
  method: string;
  brand: string | null;
  last4: string | null;
  expiryDate: string | null;
  holderName: string | null;
}

export interface AutoRechargeSettings {
  fk_workspace: string;
  mandate_id: string | null;
  enabled: boolean;
  threshold_amount: number | null;
  recharge_amount: number | null;
  last_recharge_at: string | null;
  disabled_reason: string | null;
  payment_method: SavedPaymentMethod | null;
}

export interface BillingEntityDto {
  legalName: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
  vatId?: string | null;
  billingEmail: string;
}

export interface BillingEntityRow {
  id: string;
  legal_name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country_code: string;
  vat_id: string | null;
  billing_email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InvoiceSummary {
  id: string;
  number: string | null;
  issuedAt: string | null;
  amount: string | null;
  pdfUrl: string | null;
}

/**
 * @file accounting.ts
 * Class for the Accounting client
 */
export class AccountingApi {

  private transactionsEndpoint = '/transactions'
  private balanceEndpoint = '/balances'
  private paymentsEndpoint = '/payments'
  private billingEntitiesEndpoint = '/billing-entities'

  constructor(private http: HttpClient) {
  }

  /**
   * Return balance from User
   * @returns Balance
   */
  public getBalance(workspace_id: string): Observable<Balance> {
    return this.http.get<Balance>(`${this.balanceEndpoint}/${workspace_id}`);
  }

  /**
   * List a page of accounting transactions for a workspace, newest first.
   * @returns { data: Transaction[], total: number }
   */
  public listTransactions(
    workspace_id: string,
    options?: { limit: number; offset: number },
  ): Observable<{ data: Transaction[]; total: number }> {
    return this.http.get<{ data: Transaction[]; total: number }>(
      `${this.transactionsEndpoint}/${workspace_id}`,
      { params: options ? { limit: options.limit, offset: options.offset } : undefined },
    );
  }

  /**
   * Read single transaction
   * @param id
   * @returns Transaction
  */
  public readTransaction(id: string): Observable<Transaction> {
    return this.http.get<Transaction>(`${this.transactionsEndpoint}/${id}`)
  }

  /**
   * Search for transactions of workspace
   * @param workspace_id
   * @param searchOptions
   * @returns Transaction[]
   */
  public search(workspace_id: string, searchOptions: SearchOptions = {}): Observable<Transaction[]> {
    let params = new HttpParams().set('workspace', workspace_id);
    if (searchOptions.column) params = params.set('column', searchOptions.column);
    if (searchOptions.value) params = params.set('value', searchOptions.value);
    if (searchOptions.limit != null) params = params.set('limit', searchOptions.limit);
    if (searchOptions.offset != null) params = params.set('offset', searchOptions.offset);
    return this.http.get<Transaction[]>(`${this.transactionsEndpoint}/search`, { params })
  }

  /**
   * Create a one-off top-up and return the checkout URL. The provider
   * defaults to Mollie (card/bank); pass 'nowpayments' for crypto.
   * @returns { checkoutUrl: string }
   */
  public createTopup(
    workspace_id: string,
    amount: number,
    provider: 'mollie' | 'nowpayments' = 'mollie',
  ): Observable<{ checkoutUrl: string }> {
    return this.http.post<{ checkoutUrl: string }>(
      `${this.paymentsEndpoint}/topup`,
      { fk_workspace: workspace_id, amount, provider },
    );
  }

  /**
   * Create a 'first' sequence Mollie payment to save a payment method.
   * Credits the balance AND establishes a mandate for future recurring charges.
   * @returns { checkoutUrl: string }
   */
  public saveMethod(
    workspace_id: string,
    amount: number,
  ): Observable<{ checkoutUrl: string }> {
    return this.http.post<{ checkoutUrl: string }>(
      `${this.paymentsEndpoint}/method`,
      { fk_workspace: workspace_id, amount },
    );
  }

  /**
   * Read auto-recharge settings for a workspace.
   */
  public getAutoRecharge(workspace_id: string): Observable<AutoRechargeSettings> {
    return this.http.get<AutoRechargeSettings>(
      `${this.paymentsEndpoint}/auto-recharge`,
      { params: { workspace: workspace_id } },
    );
  }

  /**
   * Update auto-recharge settings for a workspace.
   */
  public updateAutoRecharge(
    workspace_id: string,
    settings: Partial<Pick<AutoRechargeSettings, 'enabled' | 'threshold_amount' | 'recharge_amount'>>,
  ): Observable<AutoRechargeSettings> {
    return this.http.put<AutoRechargeSettings>(
      `${this.paymentsEndpoint}/auto-recharge`,
      { fk_workspace: workspace_id, ...settings },
    );
  }

  /**
   * Revoke the stored mandate and disable auto-recharge.
   */
  public removeMethod(workspace_id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.paymentsEndpoint}/method`,
      { params: { workspace: workspace_id } },
    );
  }

  /**
   * Fetch the active billing entity for a workspace (nullable).
   */
  public getBillingEntity(workspace_id: string): Observable<BillingEntityRow | null> {
    return this.http.get<BillingEntityRow | null>(
      `${this.billingEntitiesEndpoint}/${workspace_id}`,
    );
  }

  /**
   * Create or update the billing entity for a workspace.
   * POST when no entity exists, PUT when one does (pass entity id).
   */
  public saveBillingEntity(
    workspace_id: string,
    dto: BillingEntityDto,
    entityId?: string,
  ): Observable<BillingEntityRow> {
    if (entityId) {
      return this.http.put<BillingEntityRow>(
        `${this.billingEntitiesEndpoint}/${workspace_id}/${entityId}`,
        dto,
      );
    }
    return this.http.post<BillingEntityRow>(
      `${this.billingEntitiesEndpoint}/${workspace_id}`,
      dto,
    );
  }

  /**
   * List Mollie sales invoices for the workspace's active billing entity.
   * Returns [] when no entity exists or the beta endpoint is unavailable.
   */
  public listInvoices(workspace_id: string): Observable<InvoiceSummary[]> {
    return this.http.get<InvoiceSummary[]>(
      `${this.paymentsEndpoint}/invoices`,
      { params: { workspace: workspace_id } },
    );
  }
}
